import { createFileRoute, Outlet } from '@tanstack/react-router'
import { teamChromeSearchDeps, prefetchTeamChrome } from '@/hooks/use-team'

/**
 * Layout for a selected team (`/teams/$teamId/*`). It renders the animated tab Outlet (real content),
 * so per AGENTS.md it OWNS the teamId-keyed chrome prefetch (getTeam + members list) that all five tab
 * children read — the leaves no longer duplicate it.
 *
 * `prefetchReport` is NOT lifted here: only report/board/distribution read the report, while
 * evaluate/evaluation do not, so it stays in those three leaves (lifting it would fire an unused report
 * fetch on the evaluate/evaluation tabs).
 *
 * The `AnimatedOutlet` now lives in the parent `teams.tsx` (wrapping the `<Outlet/>` that renders this
 * layout) so the enter animation plays once when the report panel first appears and never re-animates on
 * tab/team switches; this layout just renders a plain `<Outlet/>` for its tab children.
 */
export const Route = createFileRoute('/_app/teams/$teamId')({
  loaderDeps: ({ search }) => teamChromeSearchDeps(search),
  loader: ({ context, deps, params }) => {
    prefetchTeamChrome(context.queryClient, deps, params)
  },
  component: RouteComponent,
  // No pendingComponent: this passthrough layout just renders <AnimatedOutlet/>. The data-cold case is
  // covered by the parent teams.tsx (<Suspense fallback={ReportPanelSkeleton}>), and each tab leaf
  // (report/board/distribution) owns its own tab-specific skeleton — a generic one here would mismatch.
})

function RouteComponent() {
  return <Outlet />
}
